/**
 * Design tokens for the app-wide **Modal & Dialog System**.
 *
 * Source of truth: `Modal System.dc.html` in the Inkfree design project. Every
 * overlay in the app is built from the same five slots described there — scrim,
 * header, body, footer, shell — so these values live here once rather than being
 * restated per screen.
 *
 * Two deliberate departures from the mock, both carried over from the WCAG pass
 * the rest of the app has already had:
 *
 * - The mock's muted ink `#8A85A0` sits at 3.53:1 on white and fails AA for the
 *   12.5px subtitle. `#6B6584` (5.50:1) is the substitute the re-skinned screens
 *   already use.
 * - The mock's `#E1500F` primary fill gives white label text 3.92:1, under the
 *   4.5:1 AA floor. Primary buttons therefore take the app brand red `#dd3824`
 *   (4.50:1) through the global `MuiButton` theme. `signer.orange.main` itself
 *   now tracks the same brand red (not the mock's orange) app-wide, so the
 *   icon tile glyph below reads brand red too, not a separate orange accent.
 *
 * A third departure, specific to `amber`: the mock's plan-limit gold `#B47207`
 * is 3.92:1 on white — the same shortfall as the orange above, and for the same
 * reason needs a substitute for anything that renders as *text* (button label,
 * banner headline/badge). Unlike `default`, amber has no existing app-wide brand
 * colour to borrow, so `amber.fill` mints a darkened gold (`#8A5C09`, 5.8:1)
 * instead. `tone.amber.glyph` keeps the lighter mock value for the icon tile,
 * which only has to clear the 3:1 non-text bar.
 */

import { button, contentCard, formField, notification, signer, white } from "theme/colors";

/** Tone drives the header icon tile and the primary button only. */
export type ModalTone = "default" | "destructive" | "success" | "neutral" | "amber";

/** Width step. Padding and type are constant across all of them. */
export type ModalSize = "sm" | "md" | "lg" | "xl";

export const appModalTokens = {
  color: {
    /** Title, close glyph on hover, body emphasis. */
    ink: signer.ink,
    /** Default body copy. 6.80:1 on white. */
    body: "#5D5775",
    /** Subtitle, close glyph, secondary meta. 5.50:1 on white. */
    muted: signer.text.meta,
    surface: white,
    /** Shell hairline. */
    border: button.secondaryBorder,
    /** Footer divider. */
    hairline: contentCard.border,
    /** Footer bar fill — a hair off white so the divider reads. */
    footerBg: formField.bg,
    closeHoverBg: signer.surface.muted,
    /** Inner surfaces inside the body (summary blocks, wells, read-only rows). */
    innerSurface: "#FAFAF7",
    innerBorder: "#EFEDE6",
    /** Bottom-sheet drag handle. */
    grabber: "#DCD7E8",
  },

  /** Scrim: flat wash plus a light blur so the page behind stays legible. */
  scrim: {
    background: "rgba(33, 21, 71, 0.42)",
    blur: "blur(3px)",
  },

  /**
   * Per-tone icon tile. `glyph` also colours the tile's stroke; the primary
   * button fill is handled separately (see `primaryFill`) because only the
   * destructive tone moves it off the brand red.
   */
  tone: {
    default: { tile: signer.orange.tint, glyph: signer.orange.main },
    destructive: { tile: notification.error.bg, glyph: "#D93025" },
    success: { tile: notification.success.bg, glyph: "#1E8A55" },
    neutral: { tile: "#F0ECF8", glyph: signer.purple.main },
    /** Plan-limit / "upgrade required" — see the WCAG note above. */
    amber: { tile: "#FDF1D8", glyph: signer.warn.main },
  } as Record<ModalTone, { tile: string; glyph: string }>,

  /**
   * Destructive primary fill. White on `#D93025` is 4.77:1, so unlike the mock's
   * orange this one needs no substitution. Hover holds the resting fill and the
   * press state darkens, matching the global contained-button behaviour.
   */
  destructive: {
    fill: "#D93025",
    activeFill: "#A81E17",
  },

  /**
   * The darkened gold, for gold **text on a light tint** — the badge label and
   * the gate headline. It is no longer a button fill: a solid gold button is an
   * orange fill by another name, and those go on the brand colour, leaving
   * amber to the accents.
   */
  amber: {
    fill: "#8A5C09",
    activeFill: "#6E4A07",
  },

  /** Width steps. `xl` extends the mock's S/M/L for pickers and viewers. */
  size: {
    sm: 420,
    md: 520,
    lg: 720,
    xl: 960,
  } as Record<ModalSize, number>,

  radius: {
    shell: "16px",
    /** Bottom sheet only rounds its top edge. */
    sheet: "20px 20px 0 0",
    iconTile: "11px",
    close: "8px",
    inner: "12px",
    grabber: "999px",
  },

  shadow: {
    shell: "0 24px 60px rgba(33, 21, 71, 0.28)",
    sheet: "0 -12px 44px rgba(33, 21, 71, 0.24)",
  },

  font: {
    title: "17px",
    /** The sheet's title steps down a touch. */
    sheetTitle: "16.5px",
    subtitle: "12.5px",
    sheetSubtitle: "12px",
    body: "13.5px",
  },

  weight: {
    heading: 800,
    strong: 700,
    medium: 600,
  },

  letterSpacing: {
    title: "-0.01em",
  },

  header: {
    padding: "24px 24px 0",
    sheetPadding: "12px 20px 0",
    /** Icon tile ↔ title ↔ close. */
    gap: "14px",
    titleGap: "3px",
    tileSize: 40,
    tileGlyphSize: 19,
    closeSize: 30,
    closeGlyphSize: 16,
  },

  body: {
    padding: "18px 24px 22px",
    sheetPadding: "14px 20px 18px",
    /** Body scrolls past this; the header and footer stay put. */
    maxHeight: "60vh",
    lineHeight: 1.6,
  },

  footer: {
    padding: "16px 24px",
    sheetPadding: "12px 20px 18px",
    gap: "10px",
  },

  sheet: {
    grabberWidth: 40,
    grabberHeight: 4,
    grabberPadding: "10px 0 2px",
    /** Leaves the scrim visible above a tall sheet. */
    maxHeight: "92vh",
  },

  motion: {
    shellIn: "160ms cubic-bezier(.2, .8, .3, 1)",
    sheetIn: "220ms cubic-bezier(.2, .8, .3, 1)",
    scrimIn: "140ms ease-out",
    /** MUI's own transition — kept in step with the scrim fade. */
    transitionDuration: 140,
  },
} as const;

/**
 * The mock switches to a bottom sheet at 768px, which falls between this app's
 * `sm` (600) and `md` (900) breakpoints — hence an explicit query rather than a
 * `theme.breakpoints` call.
 */
export const SHEET_QUERY = "@media (max-width:767.98px)";

/** Same threshold, in the form `useMediaQuery` wants. */
export const SHEET_MEDIA = "(max-width:767.98px)";

export default appModalTokens;
